import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteLayout } from "@/components/SiteLayout";

const TITLE = "Metodologia de cálculo — TrajetoFácil";
const DESCRIPTION =
  "Fórmulas, fontes de dados e limitações do TrajetoFácil: como calculamos distância, combustível, pedágio por eixos, ida e volta, custo total, custo por km e multiplicador.";
const URL = "https://www.trajetofacil.com.br/metodologia";

export const Route = createFileRoute("/metodologia")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: MethodologyPage,
});

function MethodologyPage() {
  return (
    <SiteLayout>
      <article className="space-y-10 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">Metodologia de cálculo</h1>
          <p className="mt-3 text-muted-foreground">
            Toda estimativa do TrajetoFácil vem de uma fórmula explícita e de uma fonte identificável.
            Esta página descreve cada conta, de onde vêm os dados, com que frequência são revisados e
            quais são os limites conhecidos do método.
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Distância e tempo</h2>
          <p>
            Origem, paradas e destino são convertidos em coordenadas geográficas. Sobre essas coordenadas
            é traçada a rota rodoviária, de onde saem a distância em quilômetros e o tempo estimado de
            cada trecho. O tempo considera condições normais de tráfego e não inclui paradas, refeições
            nem congestionamentos pontuais.
          </p>
          <p>
            Quando a opção ida e volta está ativa, os dois sentidos são calculados como rotas
            independentes — o caminho de volta pode diferir por conversões, acessos e retornos.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Combustível</h2>
          <p>
            A fórmula é direta e aplicada trecho a trecho: litros = distância ÷ consumo (km/l); custo =
            litros × preço do litro. Os valores de consumo e preço são os que você informa — por isso a
            precisão do resultado depende de usar o consumo real do veículo e o preço que você paga.
          </p>
          <p>
            Se preço do litro e km/l ficam em branco e apenas o valor por km é preenchido, o cálculo passa
            a ser distância × valor por km, mantendo o pedágio como parcela separada.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Pedágio</h2>
          <p>
            A geometria da rota é cruzada com uma base própria de praças de pedágio georreferenciadas.
            Cada praça cujo ponto coincide com o traçado, dentro de uma tolerância de posição, entra no
            cálculo com a tarifa da categoria selecionada.
          </p>
          <p>
            As categorias seguem as regras das concessões: carro paga a tarifa base, moto costuma pagar
            metade, e ônibus e caminhões pagam a tarifa multiplicada pelo número de eixos. Praças gêmeas —
            uma estrutura em cada sentido — são tratadas separadamente para não duplicar cobrança em um
            mesmo sentido, e praças de sentido único entram apenas no trecho correspondente.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Custo total, custo por km e multiplicador</h2>
          <p>
            O custo total é a soma de combustível e pedágio de todos os trechos, incluindo a volta quando
            marcada. O custo por quilômetro é esse total dividido pela distância total percorrida.
          </p>
          <p>
            O multiplicador de repasse aplica um fator sobre o custo: fator 1,30 corresponde a 30% de
            margem. O resultado aparece como valor a repassar, sem alterar o custo original exibido — as
            duas informações convivem para você comparar.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Fontes dos dados</h2>
          <ul className="list-disc space-y-1.5 pl-6">
            <li>
              <strong>ANTT</strong> — tarifas das praças em rodovias federais concedidas, a partir dos
              dados abertos da agência;
            </li>
            <li>
              <strong>ARTESP e demais reguladores estaduais</strong> — tabelas tarifárias das concessões
              estaduais, com destaque para a malha paulista;
            </li>
            <li>
              <strong>Concessionárias</strong> — tabelas publicadas por concessionária para praças e
              pórticos não cobertos pelas planilhas dos reguladores;
            </li>
            <li>
              <strong>Bases cartográficas abertas</strong> — geocodificação de endereços e traçado da malha
              rodoviária;
            </li>
            <li>
              <strong>Você</strong> — divergências reportadas por usuários são conferidas na fonte oficial e
              corrigidas na base.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Atualização dos dados</h2>
          <p>
            Uma rotina automática revisa a base de tarifas semanalmente, buscando reajustes publicados
            pelas fontes oficiais. Além disso, a equipe roda revisões manuais quando há reajuste anunciado,
            nova concessão, mudança de traçado ou reporte de divergência.
          </p>
          <p>
            Reajustes costumam ocorrer uma vez por ano por contrato de concessão, o que torna as tarifas
            estáveis pela maior parte do ano — a principal razão pela qual uma estimativa baseada em tabela
            oficial fica próxima do valor cobrado na praça.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Limitações conhecidas</h2>
          <p>
            Todo valor apresentado é estimativa de referência. Os fatores abaixo podem fazer o valor real
            divergir:
          </p>
          <ul className="list-disc space-y-1.5 pl-6">
            <li>Reajuste tarifário muito recente, ainda não refletido na base;</li>
            <li>Descontos por tag, frequência ou programas da concessionária;</li>
            <li>Pórticos free flow com tarifação proporcional ao trecho percorrido;</li>
            <li>Regras de eixo suspenso e categorias especiais de veículos comerciais;</li>
            <li>Desvios, obras e bloqueios que mudem o caminho efetivamente percorrido;</li>
            <li>Consumo real diferente do informado por conta de carga, relevo ou velocidade;</li>
            <li>Oscilação do preço do combustível entre a simulação e a viagem.</li>
          </ul>
          <p>
            Encontrou uma diferença? Reporte pela{" "}
            <Link to="/contato" className="font-medium text-primary underline underline-offset-4">
              página de contato
            </Link>
            : divergências confirmadas entram na base na atualização seguinte.
          </p>
        </section>
      </article>
    </SiteLayout>
  );
}
